import { Component } from 'react'
import styled, { css } from 'styled-components'

import * as themeUtilities from '@/utilities/client-components/utilities/themeUtils'

import { Input } from './Input'
import { SpacingPropTypes } from './PropTypes'
import RangeHashMarks from './RangeHashMarks'
import { StyledInputContainer } from './StyledInput'
import withSpacing from './WithSpacing'

const RangedTrackStyles = css`
  width: 100%;
  height: 2px;
  cursor: pointer;
  z-index: 2;
`

const RangedThumbStyles = css`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: ${({ theme }: any) => themeUtilities.getColor(theme, 'default', 'link')};
  border: 2px solid ${({ theme }: any) => themeUtilities.getColor(theme, 'default', 'link')};
  cursor: pointer;
  z-index: 2;
`

const MsFillStyles = css`
  height: 2px;
  background: ${({ theme }: any) => themeUtilities.getColor(theme, 'default', 'light')};
`

const NotNullableThumb = css`
  &::-webkit-slider-thumb {
    appearance: none;
    margin: -8px 0 0;
    ${RangedThumbStyles};
  }

  &::-moz-range-thumb {
    margin: -8px 0 0;
    ${RangedThumbStyles};
  }

  &::-ms-thumb {
    ${RangedThumbStyles};
  }
`

const NullableThumb = css`
  &::-webkit-slider-thumb {
    display: none;
    background-color: transparent;
    border: none;
  }

  &::-moz-range-thumb {
    display: none;
    background-color: transparent;
    border: none;
  }

  &::-ms-thumb {
    display: none;
  }
`

const StyledRangeInput = styled.input`
  width: 100%;
  padding: 0;
  margin: 13px 0 10px;
  appearance: none;
  cursor: pointer;
  z-index: 1;
  color: ${({ theme }: any) => themeUtilities.getColor(theme, 'default', 'link')};

  ${(props: any) => (props.nullable && props.value === '' ? NullableThumb : NotNullableThumb)};
  &::-webkit-slider-runnable-track {
    background: ${({ theme }: any) => themeUtilities.getColor(theme, 'default', 'light')};
    ${RangedTrackStyles};
  }

  &::-moz-range-track {
    background: ${({ theme }: any) => themeUtilities.getColor(theme, 'default', 'light')};
    ${RangedTrackStyles};
  }

  &::-ms-track {
    ${RangedTrackStyles};
    background: transparent;
    border-color: transparent;
    border-width: 10px 0;
    color: transparent;
  }

  &::-ms-fill-lower {
    ${MsFillStyles};
  }

  &::-ms-fill-upper {
    ${MsFillStyles};
  }

  &:focus {
    background: none;
    outline: none;
  }

  &:focus::-ms-fill-lower {
    ${MsFillStyles};
  }

  &:focus::-ms-fill-upper {
    ${MsFillStyles};
  }

  &.edge {
    &::-ms-fill-lower {
      margin-bottom: 4px;
    }

    ,
    &::-ms-fill-upper {
      margin-bottom: 4px;
    }

    ,
    &:focus::-ms-fill-lower {
      margin-bottom: 4px;
    }

    ,
    &:focus::-ms-fill-upper {
      margin-bottom: 4px;
    }
  }

  &.firefox {
    height: 0;
  }
`

interface InputRangeProps {
  color?: string
  hashmarks?: object[]
  id: string
  isRequired?: boolean
  isSubtle?: boolean
  label?: string
  max?: number
  min?: number
  nullable?: boolean
  registerInputCallback(...args: unknown[]): unknown
  spacing?: SpacingPropTypes
  step?: string | number
}

class InputRange extends Component<InputRangeProps> {
  static displayName = 'InputRange'

  static defaultProps = {
    isRequired: false,
    value: '',
    placeholder: '',
    nullable: false,
    step: 1
  }

  browserClass: any

  constructor(props: any) {
    super(props)

    this.browserClass = this.generateBrowserSelectionClass()
  }

  // We have to do this because there is a ton of finicky CSS
  generateBrowserSelectionClass() {
    const agent = window.navigator.userAgent
    if (/trident/gi.test(agent) && /11/gi.test(agent)) {
      return 'ie11'
    } else if (/edge/gi.test(agent)) {
      return 'edge'
    } else if (/firefox/gi.test(agent)) {
      return `firefox`
    }

    return ''
  }

  render() {
    const { hashmarks, min, max, nullable, step, label, isRequired } = this.props

    return (
      // @ts-expect-error
      <Input
        {...this.props}
        render={(setValue, value, error) => {
          // value is handled by input, but range is slightly odd
          if (value === '' && !nullable) {
            // Set the value to the first item if it's not nullable but the value is unset
            // Typically this would be defaulted by the browser, but for consistency across browsers we set it manually
            // @ts-expect-error
            setValue(min)
          }

          return (
            // @ts-expect-error
            <StyledInputContainer label={label} required={isRequired} error={error}>
              <StyledRangeInput
                {...this.props}
                className={this.browserClass}
                nullable={nullable}
                type="range"
                // @ts-expect-error
                value={value}
                // @ts-expect-error
                onChange={e => setValue(e.target.value.toString())}
              />
              {!!hashmarks && (
                <RangeHashMarks
                  className={this.browserClass}
                  // @ts-expect-error
                  selectedValue={value}
                  // @ts-expect-error
                  hashmarks={hashmarks}
                  min={min}
                  max={max}
                  step={step}
                  // @ts-expect-error
                  onClick={e => setValue(e.toString())}
                />
              )}
            </StyledInputContainer>
          )
        }}
      />
    )
  }
}

export default withSpacing(InputRange)
